import { TravelCurrencyCode } from '@graphql/generated';
import { CurrencyCode } from '@shared/hooks/useGeoLocale';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';

// Case-sensitive Map avoids object key coercion
const CURRENCY_CODE_TO_TRAVEL_CURRENCY_CODE = new Map<CurrencyCodeType, TravelCurrencyCode>([
  ['USD', TravelCurrencyCode.USD],
  ['EUR', TravelCurrencyCode.EUR],
  ['GBP', TravelCurrencyCode.GBP],
  ['CAD', TravelCurrencyCode.CAD],
  ['AUD', TravelCurrencyCode.AUD],
  ['JPY', TravelCurrencyCode.JPY],
]);

/**
 * Converts a currency code to TravelCurrencyCode for GraphQL queries.
 * Tries the legacy CurrencyCode→TravelCurrencyCode map first, then
 * falls back to checking if the code is directly a valid TravelCurrencyCode.
 *
 * @returns TravelCurrencyCode for GraphQL, defaults to USD if not found
 */
export const mapCurrencyCodeToTravelCurrencyCode = (currency: CurrencyCodeType): TravelCurrencyCode => {
  const mapped = CURRENCY_CODE_TO_TRAVEL_CURRENCY_CODE.get(currency);
  if (mapped) return mapped;

  if (isTravelCurrencyCode(currency)) return currency;

  return TravelCurrencyCode.USD;
};

// Reverse mapping: TravelCurrencyCode → CurrencyCode (for GraphQL → locale conversion)
const TRAVEL_CURRENCY_CODE_TO_CURRENCY_CODE = new Map<TravelCurrencyCode, CurrencyCode>([
  [TravelCurrencyCode.USD, CurrencyCode.USD],
  [TravelCurrencyCode.EUR, CurrencyCode.EUR],
  [TravelCurrencyCode.GBP, CurrencyCode.GBP],
  [TravelCurrencyCode.CAD, CurrencyCode.CAD],
  [TravelCurrencyCode.AUD, CurrencyCode.AUD],
  [TravelCurrencyCode.JPY, CurrencyCode.JPY],
]);

export const mapTravelCurrencyCodeToCurrencyCode = (travelCurrency: TravelCurrencyCode): CurrencyCode => {
  return TRAVEL_CURRENCY_CODE_TO_CURRENCY_CODE.get(travelCurrency) ?? CurrencyCode.USD;
};

export const isTravelCurrencyCode = (value: unknown): value is TravelCurrencyCode => {
  return typeof value === 'string' && (Object.values(TravelCurrencyCode) as string[]).includes(value);
};

// Returns the first valid TravelCurrencyCode from the spread args; last arg is the fallback default
export function validTravelCurrencyCode<T extends string>(...valuesAndDefault: [...Maybe<T>[], TravelCurrencyCode]): TravelCurrencyCode {
  const values = valuesAndDefault.slice(0, -1);
  const defaultValue = valuesAndDefault[valuesAndDefault.length - 1] as TravelCurrencyCode;
  for (const value of values) {
    if (value != null && isTravelCurrencyCode(value)) {
      return value as TravelCurrencyCode;
    }
  }
  return defaultValue;
}
